import { c as _c } from "react/compiler-runtime";
import { revealOnHoverClassName, touchTargetClassName } from "@/components/ui/control-surface";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";

// Square pill for toolbars, rows and headers. `label` is the accessible name and
// the tooltip, so every icon-only control is named. `chip` at size 14 is the
// remove control inside a Badge; `segment` at size `segment` is the remove
// segment of a FilterChip.
const iconButtonVariants = cva(["relative inline-flex shrink-0 cursor-pointer select-none items-center justify-center", "rounded-full border border-transparent outline-none transition duration-150 hover:duration-0", "focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-accent", "disabled:pointer-events-none disabled:opacity-disabled aria-disabled:opacity-disabled", "[&_svg]:pointer-events-none [&_svg]:shrink-0"], {
  variants: {
    variant: {
      ghost: ["bg-transparent text-text-tertiary hover:bg-fill-hover-subtle hover:text-text-primary", "data-[state=open]:bg-fill-hover-subtle data-[state=open]:text-text-primary"],
      toggle: ["bg-transparent text-text-tertiary hover:bg-fill-hover-subtle hover:text-text-primary", "aria-pressed:bg-fill-hover-subtle aria-pressed:text-text-primary"],
      toolbar: ["bg-bg-control text-text-tertiary [box-shadow:lch(0_0_0/0.105)_0px_0px_0px_1px,var(--elevation-low)]", "hover:bg-fill-pill-hover hover:text-text-primary", "hover:[box-shadow:lch(0_0_0/0.15)_0px_0px_0px_1px,var(--elevation-low)]", "data-[state=open]:bg-fill-pill-hover data-[state=open]:text-text-primary"],
      secondary: ["bg-bg-surface text-text-tertiary [box-shadow:lch(0_0_0/0.103)_0px_0px_0px_1px,var(--elevation-low)]", "hover:bg-fill-pill-hover hover:text-text-primary", "hover:[box-shadow:lch(0_0_0/0.147)_0px_0px_0px_1px,var(--elevation-low)]", "data-[state=open]:bg-fill-pill-hover data-[state=open]:text-text-primary"],
      accent: "bg-accent text-text-on-accent [box-shadow:var(--elevation-low)] hover:bg-accent-hover active:bg-accent-pressed",
      danger: "bg-red-light text-white [box-shadow:var(--elevation-low)] hover:bg-red-hover",
      dangerGhost: "bg-transparent text-red-text hover:bg-red-bg-subtle",
      success: "bg-green-light text-white [box-shadow:var(--elevation-low)] hover:bg-green-hover",
      warning: "bg-yellow-light text-gray-900 [box-shadow:var(--elevation-low)] hover:bg-yellow-hover",
      neutral: "bg-gray-700 text-white [box-shadow:var(--elevation-low)] hover:bg-gray-800",
      media: "bg-white/10 text-white [box-shadow:inset_0_0_0_1px_rgb(255_255_255/0.15)] hover:bg-white/20",
      chip: "rounded-checkbox bg-transparent opacity-60 hover:bg-black/5 hover:opacity-100 focus-visible:opacity-100",
      segment: ["rounded-none border-0 bg-transparent text-text-tertiary", "hover:bg-fill-pill-hover hover:text-text-primary focus-visible:-outline-offset-2"]
    },
    size: {
      14: "size-3.5 [&_svg]:size-3",
      24: "size-6 [&_svg]:size-3",
      28: "size-7 [&_svg]:size-3.5",
      32: "size-8 [&_svg]:size-3.5",
      segment: "h-full w-6 [&_svg]:size-3"
    },
    reveal: {
      true: revealOnHoverClassName,
      false: ""
    }
  },
  defaultVariants: {
    variant: "ghost",
    size: 28,
    reveal: false
  }
});
export type IconButtonProps = {
  label: string;
  asChild?: boolean;
  isLoading?: boolean;
  showTooltip?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & VariantProps<typeof iconButtonVariants>;

/** Use when an action is shown as an icon only; the label becomes the accessible name and tooltip. */
export function IconButton(t0) {
  const $ = _c(40);
  let children;
  let className;
  let disabled;
  let label;
  let props;
  let ref;
  let reveal;
  let size;
  let t1;
  let t2;
  let t3;
  let variant;
  if ($[0] !== t0) {
    ({
      label,
      variant,
      size,
      reveal,
      asChild: t1,
      isLoading: t2,
      showTooltip: t3,
      className,
      children,
      disabled,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = disabled;
    $[4] = label;
    $[5] = props;
    $[6] = ref;
    $[7] = reveal;
    $[8] = size;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = variant;
  } else {
    children = $[1];
    className = $[2];
    disabled = $[3];
    label = $[4];
    props = $[5];
    ref = $[6];
    reveal = $[7];
    size = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    variant = $[12];
  }
  const asChild = t1 === undefined ? false : t1;
  const isLoading = t2 === undefined ? false : t2;
  const showTooltip = t3 === undefined ? true : t3;
  const Comp = asChild ? Slot : "button";
  const t4 = asChild ? undefined : "button";
  let t5;
  if ($[13] !== className || $[14] !== reveal || $[15] !== size || $[16] !== variant) {
    t5 = cn(iconButtonVariants({
      variant,
      size,
      reveal
    }), touchTargetClassName, className);
    $[13] = className;
    $[14] = reveal;
    $[15] = size;
    $[16] = variant;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const t6 = disabled || isLoading;
  const t7 = isLoading || undefined;
  let t8;
  if ($[18] !== asChild || $[19] !== children || $[20] !== isLoading || $[21] !== size) {
    t8 = isLoading && !asChild ? <Spinner size={size === 24 ? "xs" : "sm"} /> : children;
    $[18] = asChild;
    $[19] = children;
    $[20] = isLoading;
    $[21] = size;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== Comp || $[24] !== label || $[25] !== props || $[26] !== ref || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = <Comp ref={ref} type={t4} aria-label={label} className={t5} disabled={t6} aria-busy={t7} {...props}>{t8}</Comp>;
    $[23] = Comp;
    $[24] = label;
    $[25] = props;
    $[26] = ref;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const button = t9;
  if (!showTooltip) {
    return button;
  }
  let t10;
  if ($[33] !== button) {
    t10 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[33] = button;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== label) {
    t11 = <TooltipContent className="!pointer-events-none">{label}</TooltipContent>;
    $[35] = label;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== t10 || $[38] !== t11) {
    t12 = <Tooltip>{t10}{t11}</Tooltip>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}
export { iconButtonVariants };